/**
 * The workstation lock's decision and the small localStorage state every tab shares
 * (workstation-lock plan T5). No React or Firebase imports, same design rules as sessionPolicy.
 */

/** How often the idle clock is checked, and how often a tab writes that it was used. */
export const IDLE_CHECK_INTERVAL_MS = 15_000;
export const ACTIVITY_WRITE_INTERVAL_MS = 5_000;

const LAST_ACTIVITY_KEY = 'st.idle.lastActivityAt';
const LOCKED_KEY = 'st.idle.locked';

export interface LockInput {
  lastActivityAt: number;
  now: number;
  /** The clinic's idle limit; null means the lock is off. */
  minutes: number | null;
  online: boolean;
  locked: boolean;
}

/** Has this computer sat idle for the clinic's limit? Never offline: unlocking needs the server. */
export function shouldLock({ lastActivityAt, now, minutes, online, locked }: LockInput): boolean {
  if (minutes == null || !online || locked) return false;
  return now - lastActivityAt >= minutes * 60_000;
}

/** When anyone last used this computer, in any tab, or null. */
export function readLastActivity(): number | null {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (raw == null) return null;
    const at = Number(raw);
    return Number.isFinite(at) ? at : null;
  } catch {
    return null;
  }
}

export function recordActivity(now: number): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
  } catch {
    /* storage unavailable: the idle clock simply cannot be shared */
  }
}

const listeners = new Set<() => void>();

export function isScreenLocked(): boolean {
  try {
    return localStorage.getItem(LOCKED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Locks or unlocks every tab: the storage event reaches the others, the listeners this one. */
export function setScreenLocked(locked: boolean): void {
  try {
    if (locked) localStorage.setItem(LOCKED_KEY, '1');
    else localStorage.removeItem(LOCKED_KEY);
  } catch {
    /* storage unavailable */
  }
  listeners.forEach((listener) => listener());
}

/** For useSyncExternalStore: lock changes made in this tab or in another one. */
export function subscribeScreenLock(onChange: () => void): () => void {
  listeners.add(onChange);
  const onStorage = (e: StorageEvent) => {
    if (e.key === LOCKED_KEY || e.key === null) onChange();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener('storage', onStorage);
  };
}
